import { createEntityAdapter } from '@reduxjs/toolkit';
import { twApi } from '../api';

import {
  SearchTripsParams,
  Trip,
  StopStatus,
  TripSearchResponse,
  TRIP_STATUS,
  TripStop,
  BulkParams,
  UpsertTrip,
  TripSharePreviewParams,
  ShareHistory,
  TripByOrderIdResponse,
  DriverLinks,
  UpdateTripDriverAckByToken,
  UpdateTripDriverAckByTokenResponse,
  UpdateTripDriverAck,
  UpdateTripDriverAckResponse
} from './trips.model';
import { PaginateNormalizedResponse, PaginateResponse } from '../../Global.model';
import { FilterParams } from '../savedViews/savedViews.model';
import { Driver } from '../driver/driver.model';
import { ICreateLoadTag, ISearchTagsResponse } from '../loadTags/loadTags.model';
import { SearchFieldParams } from '..';

const persistentTripsAdapter = createEntityAdapter<TripSearchResponse>({
  selectId: (trip) => trip.id,
  sortComparer: (a, b) => {
    const aTime = new Date(a.start_date).getTime();
    const bTime = new Date(b.start_date).getTime();

    return bTime - aTime;
  }
});

const persistentTripsSelector = persistentTripsAdapter.getSelectors();

interface PaginateTripsSearchResponse extends PaginateResponse<TripSearchResponse> {
  uncovered_count: number;
}

export const tripApi = twApi.injectEndpoints({
  endpoints: (builder) => ({
    getTrips: builder.query<PaginateTripsSearchResponse, SearchTripsParams>({
      query: (searchLoadParams) => {
        const { config, filters, params } = searchLoadParams;
        const queryParams = {
          calendar: config?.calendar,
          calendarMode: config?.calendarMode,
          ...params
        };
        return {
          url: 'trips/search',
          method: 'POST',
          body: filters,
          params: queryParams
        };
      },
      transformResponse: (response: {
        data: TripSearchResponse[];
        total: number;
        more_records: boolean;
        uncovered_count: number;
      }) => {
        return {
          content: response.data,
          total: response.total,
          more_records: response.more_records,
          uncovered_count: response.uncovered_count
        };
      },
      providesTags: (result) =>
        result?.content
          ? [
              ...result.content.map(({ id }) => ({ type: 'Trips' as const, id })),
              { type: 'Trips', id: 'TRIPS_LIST' }
            ]
          : [{ type: 'Trips', id: 'TRIPS_LIST' }]
    }),
    getTripById: builder.query<Trip, string>({
      query: (id) => ({
        url: `trips/${id}`,
        method: 'GET'
      }),
      transformResponse: (response: { data: Trip }) => {
        return response.data;
      },
      providesTags: (result, error, id) => [
        { type: 'Trips', id: 'TRIPS_LIST' },
        { type: 'Trips' as const, id }
      ]
    }),
    getTripsByOrderId: builder.query<TripByOrderIdResponse[], { orderId: string }>({
      query: ({ orderId }) => ({
        url: `trips/by-order/${orderId}`,
        method: 'GET'
      }),
      transformResponse: (response: { data: TripByOrderIdResponse[] }) => {
        return response.data;
      }
    }),
    getActivePersistentTrips: builder.query<
      PaginateNormalizedResponse<TripSearchResponse>,
      SearchTripsParams
    >({
      query: (searchLoadParams) => {
        const { config, filters, params } = searchLoadParams;
        const queryParams = {
          calendar: config?.calendar,
          active: config?.active ?? true,
          ...params
        };
        return {
          url: 'trips/search',
          method: 'POST',
          body: filters,
          params: queryParams
        };
      },
      transformResponse: (response: {
        data: TripSearchResponse[];
        total: number;
        more_records: boolean;
      }) => {
        return {
          content: persistentTripsAdapter.setAll(
            persistentTripsAdapter.getInitialState(),
            response.data
          ),
          total: response.total,
          more_records: response.more_records
        };
      },
      providesTags: (result) => {
        return result?.content
          ? [
              ...persistentTripsSelector
                .selectIds(result?.content ?? persistentTripsAdapter.getInitialState())
                .map((id) => ({ type: 'ActiveTrips' as const, id })),
              { type: 'ActiveTrips', id: 'TRIPS_LIST' }
            ]
          : [{ type: 'ActiveTrips', id: 'TRIPS_LIST' }];
      },
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        return `${endpointName}-${JSON.stringify(queryArgs.filters)}`;
      },
      merge: (currentCache, incomingResponse, { arg }) => {
        const incoming = persistentTripsSelector.selectAll(incomingResponse.content);

        // if offset is zero it means we are at the initial page
        // so is the initial query fetch on page mount or
        // it means we reached the top by scrolling up
        // in both cases we should replace the current cache with the incoming data from response
        if (arg.params?.offset === 0) {
          persistentTripsAdapter.setAll(currentCache.content, incoming);
        } else {
          if (incoming.length === 0) {
            persistentTripsAdapter.setAll(currentCache.content, incoming);
          } else {
            persistentTripsAdapter.upsertMany(
              currentCache.content,
              persistentTripsSelector.selectAll(incomingResponse.content)
            );
          }
        }

        currentCache.total = incomingResponse.total;
        currentCache.more_records = incomingResponse.more_records;
      },
      forceRefetch: ({ currentArg, previousArg }) => {
        return currentArg?.params?.offset !== previousArg?.params?.offset;
      }
    }),
    patchTrip: builder.mutation<
      { data: Trip; success: boolean },
      {
        id: string;
        body: Partial<UpsertTrip>;
        tagsToInvalidate: any[];
        searchTripParams?: SearchTripsParams;
        prevStatus?: TRIP_STATUS;
      }
    >({
      query: ({ id, body }) => ({
        url: `trips/${id}`,
        method: 'PATCH',
        body: body
      }),
      onQueryStarted: (
        { id: tripId, body, searchTripParams, prevStatus },
        { dispatch, queryFulfilled }
      ) => {
        // update previous status column
        const update1 = dispatch(
          tripApi.util.updateQueryData(
            'getActivePersistentTrips',
            {
              ...searchTripParams,
              filters: { ...searchTripParams?.filters, status: [TRIP_STATUS.DRAFT] }
            },
            (tripsData) => {
              persistentTripsAdapter.removeAll(tripsData.content);
            }
          )
        );
        const update2 = dispatch(
          tripApi.util.updateQueryData(
            'getActivePersistentTrips',
            {
              ...searchTripParams,
              filters: { ...searchTripParams?.filters, status: [TRIP_STATUS.DISPATCHED] }
            },
            (tripsData) => {
              persistentTripsAdapter.removeAll(tripsData.content);
            }
          )
        );
        const update3 = dispatch(
          tripApi.util.updateQueryData(
            'getActivePersistentTrips',
            {
              ...searchTripParams,
              filters: { ...searchTripParams?.filters, status: [TRIP_STATUS.IN_TRANSIT] }
            },
            (tripsData) => {
              persistentTripsAdapter.removeAll(tripsData.content);
            }
          )
        );
        const update4 = dispatch(
          tripApi.util.updateQueryData(
            'getActivePersistentTrips',
            {
              ...searchTripParams,
              filters: { ...searchTripParams?.filters, status: [TRIP_STATUS.DELIVERED] }
            },
            (tripsData) => {
              persistentTripsAdapter.removeAll(tripsData.content);
            }
          )
        );

        queryFulfilled.catch(() => {
          update1.undo();
          update2.undo();
          update3.undo();
          update4.undo();
        });
      },
      invalidatesTags: (result, error, { tagsToInvalidate }) => {
        if (result)
          tagsToInvalidate.push(
            { type: 'Trips' as const, id: result.data.id },
            { type: 'Trips' as const, id: 'TRIP_DRIVER_LINKS' },
            { type: 'Orders' as const, id: 'ORDER_DRIVER_LINKS' }
          );
        return tagsToInvalidate;
      }
    }),
    putTrip: builder.mutation<
      Trip,
      {
        id: string;
        body: Partial<UpsertTrip>;
        searchTripParams?: SearchTripsParams;
        prevStatus?: TRIP_STATUS;
      }
    >({
      query: ({ id, body }) => ({
        url: `trips/${id}`,
        method: 'PUT',
        body: body
      }),
      onQueryStarted: (
        { id: tripId, body, searchTripParams, prevStatus },
        { dispatch, queryFulfilled }
      ) => {
        // update previous status column
        const update1 = dispatch(
          tripApi.util.updateQueryData(
            'getActivePersistentTrips',
            {
              ...searchTripParams,
              filters: { ...searchTripParams?.filters, status: [TRIP_STATUS.DRAFT] }
            },
            (tripsData) => {
              persistentTripsAdapter.removeAll(tripsData.content);
            }
          )
        );
        const update2 = dispatch(
          tripApi.util.updateQueryData(
            'getActivePersistentTrips',
            {
              ...searchTripParams,
              filters: { ...searchTripParams?.filters, status: [TRIP_STATUS.DISPATCHED] }
            },
            (tripsData) => {
              persistentTripsAdapter.removeAll(tripsData.content);
            }
          )
        );
        const update3 = dispatch(
          tripApi.util.updateQueryData(
            'getActivePersistentTrips',
            {
              ...searchTripParams,
              filters: { ...searchTripParams?.filters, status: [TRIP_STATUS.IN_TRANSIT] }
            },
            (tripsData) => {
              persistentTripsAdapter.removeAll(tripsData.content);
            }
          )
        );
        const update4 = dispatch(
          tripApi.util.updateQueryData(
            'getActivePersistentTrips',
            {
              ...searchTripParams,
              filters: { ...searchTripParams?.filters, status: [TRIP_STATUS.DELIVERED] }
            },
            (tripsData) => {
              persistentTripsAdapter.removeAll(tripsData.content);
            }
          )
        );

        queryFulfilled.catch(() => {
          update1.undo();
          update2.undo();
          update3.undo();
          update4.undo();
        });
      },
      invalidatesTags: (result, error, { id }) => [
        { type: 'Trips' as const, id },
        { type: 'Trips', id: 'TRIPS_LIST' },
        { type: 'ActiveTrips', id: 'TRIPS_LIST' },
        { type: 'Orders', id: 'ORDERS_LIST' },
        { type: 'Trips' as const, id: 'TRIP_DRIVER_LINKS' }
      ]
    }),
    patchStop: builder.mutation<
      any,
      {
        stopId: string;
        body: Partial<TripStop>;
        invalidateTags: any[];
        searchTripParams?: SearchTripsParams;
        prevStatus?: TRIP_STATUS;
      }
    >({
      query: ({ stopId, body, searchTripParams, prevStatus }) => {
        return {
          url: `stops/${stopId}`,
          method: 'PATCH',
          body: body
        };
      },
      onQueryStarted: (
        { stopId, body, searchTripParams, prevStatus },
        { dispatch, queryFulfilled }
      ) => {
        // when using this mutation, body should have trip_id
        if (!body.trip_id) return;
        const tripId = body.trip_id;

        let entity: any = null;

        // first remove from previous status
        const update1 = dispatch(
          tripApiUtil.updateQueryData(
            'getActivePersistentTrips',
            {
              ...searchTripParams,
              filters: { ...searchTripParams?.filters, status: [prevStatus] as TRIP_STATUS[] }
            },
            (tripsData) => {
              const trip = persistentTripsSelector.selectById(tripsData.content, tripId);
              entity = trip;
              persistentTripsAdapter.removeOne(tripsData.content, tripId);
            }
          )
        );

        // after query is fulfilled,check trip status from response and add to new status
        queryFulfilled
          .then(({ data: responseData }) => {
            const newStatus = [responseData.data.trip_status];
            dispatch(
              tripApiUtil.updateQueryData(
                'getActivePersistentTrips',
                {
                  ...searchTripParams,
                  filters: { ...searchTripParams?.filters, status: newStatus }
                },
                (tripsData) => {
                  persistentTripsAdapter.addOne(tripsData.content, {
                    ...entity,
                    status: responseData.status
                  });
                }
              )
            );
          })
          .catch(() => {
            update1.undo();
          });
      },
      invalidatesTags: (result, error, { invalidateTags }) => {
        if (result) {
          return [...invalidateTags, { type: 'Trips' as const, id: result.data.trip_id }];
        }

        return invalidateTags;
      }
    }),
    getTripByToken: builder.query<
      {
        trip: Trip;
        isDriverAssignedToTrip: boolean;
        driver: Driver;
        tenant: Record<string, string>;
        tenantSettings: Record<string, boolean>;
      },
      string
    >({
      query: (token) => ({
        url: `trips/t/${token}`,
        method: 'GET'
      }),
      transformResponse: (response: {
        data: {
          trip: Trip;
          driver: Driver;
          tenant: Record<string, string>;
          isDriverAssignedToTrip: boolean;
          tenantSettings: Record<string, boolean>;
        };
      }) => {
        return response.data;
      },
      providesTags: (result, error, id) => [
        { type: 'Trips' as const, id: 'TRIPS_LIST' },
        { type: 'Trips' as const, id: result?.trip.id }
      ]
    }),
    updateStopByToken: builder.mutation<
      any,
      {
        token: string;
        stopId: string;
        stopUpdatedAt: string;
        tripId?: string;
        stopStatus?: StopStatus;
        stopInTime?: string;
        stopOutTime?: string;
      }
    >({
      query: (data) => {
        const { token, tripId, stopId, stopUpdatedAt, stopStatus, stopInTime, stopOutTime } = data;

        return {
          url: `orders/t/${token}`,
          method: 'PUT',
          body: {
            trip_id: tripId,
            stop_id: stopId,
            stop_updated_at: stopUpdatedAt,
            stop_status: stopStatus,
            stop_in_time: stopInTime,
            stop_out_time: stopOutTime
          }
        };
      }
    }),
    patchEquipmentByToken: builder.mutation<
      any,
      {
        token: string;
        tripId?: string;
        equipmentKey: string;
        equipmentId: string | null;
      }
    >({
      query: (data) => {
        const { token, tripId, equipmentId, equipmentKey } = data;
        return {
          url: `orders/t/${token}/equipment`,
          method: 'PATCH',
          body: {
            trip_id: tripId,
            equipmentKey,
            equipmentId
          }
        };
      },
      invalidatesTags: (res) => {
        const tags = [
          {
            type: 'Trips' as const,
            id: 'TRIPS_LIST'
          },
          {
            type: 'Orders' as const,
            id: 'ORDERS_LIST'
          }
        ];
        if (res) tags.push({ type: 'Trips' as const, id: res.data.id });
        return tags;
      }
    }),

    bulkUpdateTrip: builder.mutation<number, BulkParams>({
      query: (data) => ({
        url: 'trips/bulk-update',
        method: 'PUT',
        body: data
      }),
      transformResponse: (_, meta: { response: { status: number } }) => {
        return meta.response.status;
      },
      onQueryStarted: ({ searchFilters }, { dispatch, queryFulfilled }) => {
        const { status, ...restFilters } = searchFilters!;

        // update all status columns
        const update1 = dispatch(
          tripApi.util.updateQueryData(
            'getActivePersistentTrips',
            { filters: { ...restFilters, status: [TRIP_STATUS.DRAFT] } },
            (tripsData) => {
              persistentTripsAdapter.removeAll(tripsData.content);
            }
          )
        );
        const update2 = dispatch(
          tripApi.util.updateQueryData(
            'getActivePersistentTrips',
            { filters: { ...restFilters, status: [TRIP_STATUS.DISPATCHED] } },
            (tripsData) => {
              persistentTripsAdapter.removeAll(tripsData.content);
            }
          )
        );
        const update3 = dispatch(
          tripApi.util.updateQueryData(
            'getActivePersistentTrips',
            { filters: { ...restFilters, status: [TRIP_STATUS.IN_TRANSIT] } },
            (tripsData) => {
              persistentTripsAdapter.removeAll(tripsData.content);
            }
          )
        );
        const update4 = dispatch(
          tripApi.util.updateQueryData(
            'getActivePersistentTrips',
            { filters: { ...restFilters, status: [TRIP_STATUS.DELIVERED] } },
            (tripsData) => {
              persistentTripsAdapter.removeAll(tripsData.content);
            }
          )
        );

        queryFulfilled.catch(() => {
          update1.undo();
          update2.undo();
          update3.undo();
          update4.undo();
        });
      },
      invalidatesTags: [
        { type: 'Trips', id: 'TRIPS_LIST' },
        { type: 'ActiveTrips', id: 'TRIPS_LIST' },
        { type: 'Orders', id: 'ORDERS_LIST' }
      ]
    }),
    createTrip: builder.mutation<Trip, UpsertTrip>({
      query: (data) => ({
        url: 'trips',
        method: 'POST',
        body: data
      }),
      transformResponse: (response: { data: Trip }) => response.data,
      invalidatesTags: [
        { type: 'Trips', id: 'TRIPS_LIST' },
        { type: 'ActiveTrips', id: 'TRIPS_LIST' },
        { type: 'Orders', id: 'ORDERS_LIST' }
      ]
    }),
    deleteTrip: builder.mutation<
      { success: boolean; message: string; data: { other_expenses_updated: number } },
      { id: string; searchFilters: FilterParams; orderId: string }
    >({
      query: ({ id }) => ({
        url: `trips/${id}`,
        method: 'DELETE'
      }),
      onQueryStarted: ({ id, searchFilters }, { dispatch, queryFulfilled }) => {
        const { status, ...restFilters } = searchFilters!;

        // update all status columns
        const update1 = dispatch(
          tripApi.util.updateQueryData(
            'getActivePersistentTrips',
            { filters: { ...restFilters, status: [TRIP_STATUS.DRAFT] } },
            (tripsData) => {
              persistentTripsAdapter.removeAll(tripsData.content);
            }
          )
        );
        const update2 = dispatch(
          tripApi.util.updateQueryData(
            'getActivePersistentTrips',
            { filters: { ...restFilters, status: [TRIP_STATUS.DISPATCHED] } },
            (tripsData) => {
              persistentTripsAdapter.removeAll(tripsData.content);
            }
          )
        );
        const update3 = dispatch(
          tripApi.util.updateQueryData(
            'getActivePersistentTrips',
            { filters: { ...restFilters, status: [TRIP_STATUS.IN_TRANSIT] } },
            (tripsData) => {
              persistentTripsAdapter.removeAll(tripsData.content);
            }
          )
        );
        const update4 = dispatch(
          tripApi.util.updateQueryData(
            'getActivePersistentTrips',
            { filters: { ...restFilters, status: [TRIP_STATUS.DELIVERED] } },
            (tripsData) => {
              persistentTripsAdapter.removeAll(tripsData.content);
            }
          )
        );

        queryFulfilled.catch(() => {
          update1.undo();
          update2.undo();
          update3.undo();
          update4.undo();
        });
      },
      invalidatesTags: (result, error, { id, orderId }) => [
        { type: 'Trips', id: 'TRIPS_LIST' },
        { type: 'ActiveTrips', id: 'TRIPS_LIST' },
        { type: 'Orders' as const, orderId },
        { type: 'Orders', id: 'ORDERS_LIST' },
        { type: 'OtherExpenses', id: 'OTHER_EXPENSES_LIST' },
        { type: 'Attachments', id: 'ATTACHMENTS_LIST' }
      ]
    }),
    canTripsBeDeleted: builder.query<
      {
        status: boolean;
        message: string;
      },
      string[]
    >({
      query: (tripsIds) => ({
        url: 'trips/can-trips-be-deleted',
        method: 'POST',
        body: {
          trips_ids: tripsIds
        }
      }),
      transformResponse: (response: { status: boolean; message: string }) => response
    }),
    bulkDeleteTrips: builder.mutation<
      { success: boolean; data: { other_expenses_updated: number } },
      string[]
    >({
      query: (tripsIds) => ({
        url: 'trips/bulk-delete',
        method: 'DELETE',
        body: {
          trips_ids: tripsIds
        }
      }),
      transformResponse: (response: {
        success: boolean;
        data: { other_expenses_updated: number };
      }) => response,
      invalidatesTags: (result, error) => [
        { type: 'Trips', id: 'TRIPS_LIST' },
        { type: 'ActiveTrips', id: 'TRIPS_LIST' },
        { type: 'OtherExpenses', id: 'OTHER_EXPENSES_LIST' },
        { type: 'Attachments', id: 'ATTACHMENTS_LIST' }
      ]
    }),
    shareTrip: builder.mutation<
      number,
      { id: string; instructions: TripSharePreviewParams; searchTripParams?: SearchTripsParams }
    >({
      query: ({ id, instructions }) => ({
        url: `trips/${id}/share-preview`,
        method: 'POST',
        body: instructions
      }),
      onQueryStarted: ({ searchTripParams }, { dispatch, queryFulfilled }) => {
        // update previous status column
        const update1 = dispatch(
          tripApi.util.updateQueryData(
            'getActivePersistentTrips',
            {
              ...searchTripParams,
              filters: { ...searchTripParams?.filters, status: [TRIP_STATUS.DRAFT] }
            },
            (tripsData) => {
              persistentTripsAdapter.removeAll(tripsData.content);
            }
          )
        );
        const update2 = dispatch(
          tripApi.util.updateQueryData(
            'getActivePersistentTrips',
            {
              ...searchTripParams,
              filters: { ...searchTripParams?.filters, status: [TRIP_STATUS.DISPATCHED] }
            },
            (tripsData) => {
              persistentTripsAdapter.removeAll(tripsData.content);
            }
          )
        );
        const update3 = dispatch(
          tripApi.util.updateQueryData(
            'getActivePersistentTrips',
            {
              ...searchTripParams,
              filters: { ...searchTripParams?.filters, status: [TRIP_STATUS.IN_TRANSIT] }
            },
            (tripsData) => {
              persistentTripsAdapter.removeAll(tripsData.content);
            }
          )
        );
        const update4 = dispatch(
          tripApi.util.updateQueryData(
            'getActivePersistentTrips',
            {
              ...searchTripParams,
              filters: { ...searchTripParams?.filters, status: [TRIP_STATUS.DELIVERED] }
            },
            (tripsData) => {
              persistentTripsAdapter.removeAll(tripsData.content);
            }
          )
        );

        queryFulfilled.catch(() => {
          update1.undo();
          update2.undo();
          update3.undo();
          update4.undo();
        });
      },
      transformResponse: (_, meta: { response: { status: number } }) => {
        return meta.response.status;
      },
      invalidatesTags: (result, error, { id }) => [
        { type: 'Trips', id: 'TRIPS_LIST' },
        { type: 'Trips', id },
        { type: 'ActiveTrips', id: 'TRIPS_LIST' }
      ]
    }),
    getTripShareHistory: builder.query<ShareHistory[], string>({
      query: (id) => ({
        url: `trips/${id}/share-history`,
        method: 'GET'
      }),
      transformResponse: (response: { data: ShareHistory[] }) => {
        return response.data;
      },
      providesTags: (result, error, id) => [{ type: 'Trips' as const, id }]
    }),
    addTripTag: builder.mutation<
      ISearchTagsResponse,
      ICreateLoadTag & { searchTripParams: SearchTripsParams }
    >({
      query: (params) => {
        const { tripId, ...rest } = params;

        return { url: `trips/${tripId}/tags`, method: 'POST', body: { ...rest } };
      },
      onQueryStarted: ({ searchTripParams }, { dispatch, queryFulfilled }) => {
        // update previous status column
        const update1 = dispatch(
          tripApi.util.updateQueryData(
            'getActivePersistentTrips',
            {
              ...searchTripParams,
              filters: { ...searchTripParams?.filters, status: [TRIP_STATUS.DRAFT] }
            },
            (tripsData) => {
              persistentTripsAdapter.removeAll(tripsData.content);
            }
          )
        );
        const update2 = dispatch(
          tripApi.util.updateQueryData(
            'getActivePersistentTrips',
            {
              ...searchTripParams,
              filters: { ...searchTripParams?.filters, status: [TRIP_STATUS.DISPATCHED] }
            },
            (tripsData) => {
              persistentTripsAdapter.removeAll(tripsData.content);
            }
          )
        );
        const update3 = dispatch(
          tripApi.util.updateQueryData(
            'getActivePersistentTrips',
            {
              ...searchTripParams,
              filters: { ...searchTripParams?.filters, status: [TRIP_STATUS.IN_TRANSIT] }
            },
            (tripsData) => {
              persistentTripsAdapter.removeAll(tripsData.content);
            }
          )
        );
        const update4 = dispatch(
          tripApi.util.updateQueryData(
            'getActivePersistentTrips',
            {
              ...searchTripParams,
              filters: { ...searchTripParams?.filters, status: [TRIP_STATUS.DELIVERED] }
            },
            (tripsData) => {
              persistentTripsAdapter.removeAll(tripsData.content);
            }
          )
        );

        queryFulfilled.catch(() => {
          update1.undo();
          update2.undo();
          update3.undo();
          update4.undo();
        });
      },
      invalidatesTags: (result, error, { tripId }) => [
        { type: 'Trips' as const, id: tripId },
        { type: 'TagsList', id: 'TAGS_LIST' },
        { type: 'Trips', id: 'TRIPS_LIST' },
        { type: 'ActiveTrips', id: 'TRIPS_LIST' }
      ]
    }),
    deleteTagFromTrip: builder.mutation<
      any,
      { tripId: string; tagId: string; searchTripParams: SearchTripsParams }
    >({
      query: ({ tripId, tagId }) => ({
        url: `trips/${tripId}/tags/${tagId}`,
        method: 'DELETE'
      }),
      onQueryStarted: ({ searchTripParams }, { dispatch, queryFulfilled }) => {
        // update previous status column
        const update1 = dispatch(
          tripApi.util.updateQueryData(
            'getActivePersistentTrips',
            {
              ...searchTripParams,
              filters: { ...searchTripParams?.filters, status: [TRIP_STATUS.DRAFT] }
            },
            (tripsData) => {
              persistentTripsAdapter.removeAll(tripsData.content);
            }
          )
        );
        const update2 = dispatch(
          tripApi.util.updateQueryData(
            'getActivePersistentTrips',
            {
              ...searchTripParams,
              filters: { ...searchTripParams?.filters, status: [TRIP_STATUS.DISPATCHED] }
            },
            (tripsData) => {
              persistentTripsAdapter.removeAll(tripsData.content);
            }
          )
        );
        const update3 = dispatch(
          tripApi.util.updateQueryData(
            'getActivePersistentTrips',
            {
              ...searchTripParams,
              filters: { ...searchTripParams?.filters, status: [TRIP_STATUS.IN_TRANSIT] }
            },
            (tripsData) => {
              persistentTripsAdapter.removeAll(tripsData.content);
            }
          )
        );
        const update4 = dispatch(
          tripApi.util.updateQueryData(
            'getActivePersistentTrips',
            {
              ...searchTripParams,
              filters: { ...searchTripParams?.filters, status: [TRIP_STATUS.DELIVERED] }
            },
            (tripsData) => {
              persistentTripsAdapter.removeAll(tripsData.content);
            }
          )
        );

        queryFulfilled.catch(() => {
          update1.undo();
          update2.undo();
          update3.undo();
          update4.undo();
        });
      },
      invalidatesTags: (result, error, { tripId }) => [
        { type: 'Trips' as const, id: tripId },

        { type: 'Trips', id: 'TRIPS_LIST' },
        { type: 'ActiveTrips', id: 'TRIPS_LIST' }
      ]
    }),
    searchTripField: builder.query<PaginateResponse<TripSearchResponse>, SearchFieldParams>({
      query: (searchTripFieldParams) => {
        const { search, field, attributes, params } = searchTripFieldParams;

        return {
          url: 'trips/fields/search',
          method: 'POST',
          body: { search, field, attributes },
          params: params
        };
      },
      transformResponse: (response: {
        data: TripSearchResponse[];
        total: number;
        more_records: boolean;
      }) => {
        return {
          content: response.data,
          total: response.total,
          more_records: response.more_records
        };
      },
      providesTags: (result) =>
        result?.content
          ? [
              ...result.content.map(({ id }) => ({ type: 'TripsField' as const, id })),
              { type: 'TripsField', id: 'TRIPS_LIST' }
            ]
          : [{ type: 'TripsField', id: 'TRIPS_LIST' }]
    }),

    patchTripByToken: builder.mutation<
      any,
      {
        token: string;
        tripId?: string;
        body: Partial<UpsertTrip>;
      }
    >({
      query: (data) => {
        const { token, tripId, body } = data;
        return {
          url: `trips/t/${token}`,
          method: 'PATCH',
          body: {
            trip_id: tripId,
            ...body
          }
        };
      },
      invalidatesTags: (res) => {
        const tags = [
          {
            type: 'Trips' as const,
            id: 'TRIPS_LIST'
          },
          {
            type: 'Orders' as const,
            id: 'ORDERS_LIST'
          }
        ];
        if (res) tags.push({ type: 'Trips' as const, id: res.data.id });
        return tags;
      }
    }),
    exportTripsCSV: builder.mutation<Blob, FilterParams>({
      query: (filters) => ({
        url: 'trips/csv',
        method: 'POST',
        body: filters,
        responseHandler: (response): any => response.text()
      }),
      transformResponse: (response: any) => {
        return new Blob([response]);
      }
    }),
    getDriverLinksForTrip: builder.query<DriverLinks, string>({
      query: (id) => ({
        url: `trips/${id}/driver-links`,
        method: 'GET'
      }),
      transformResponse: (response: { data: DriverLinks }) => {
        return response.data;
      },
      providesTags: () => [{ type: 'Trips' as const, id: 'TRIP_DRIVER_LINKS' }]
    }),
    patchTripDriverAck: builder.mutation<
      UpdateTripDriverAckResponse,
      {
        tripId: string;
        body: UpdateTripDriverAck;
      }
    >({
      query: (data) => {
        const { tripId, body } = data;
        return {
          url: `trips/${tripId}/driver-ack`,
          method: 'PATCH',
          body: {
            ...body
          }
        };
      },
      invalidatesTags: (result, error, { tripId }) => [
        { type: 'Trips' as const, id: tripId },
        { type: 'ActiveTrips', id: 'TRIPS_LIST' }
      ]
    }),
    patchTripDriverAckByToken: builder.mutation<
      UpdateTripDriverAckByTokenResponse,
      {
        token: string;
        tripId: string;
        body: UpdateTripDriverAckByToken;
      }
    >({
      query: (data) => {
        const { token, body } = data;
        return {
          url: `trips/t/${token}/driver-ack`,
          method: 'PATCH',
          body: {
            ...body
          }
        };
      },
      invalidatesTags: (result, error, { tripId }) => [{ type: 'Trips' as const, id: tripId }]
    })
  })
});

export const {
  useGetTripByIdQuery,
  useGetTripsQuery,
  useLazyGetTripsQuery,
  useGetTripsByOrderIdQuery,
  useLazyGetTripsByOrderIdQuery,
  useGetActivePersistentTripsQuery,
  usePrefetch: usePrefetchTripsQuery,
  usePatchTripMutation,
  usePutTripMutation,
  usePatchStopMutation,
  useGetTripByTokenQuery,
  useUpdateStopByTokenMutation,
  usePatchEquipmentByTokenMutation,
  useBulkUpdateTripMutation,
  useLazyGetTripByIdQuery,
  useCreateTripMutation,
  useDeleteTripMutation,
  useCanTripsBeDeletedQuery,
  useBulkDeleteTripsMutation,
  useShareTripMutation,
  useLazyGetTripShareHistoryQuery,
  useAddTripTagMutation,
  useDeleteTagFromTripMutation,
  useSearchTripFieldQuery,
  useLazySearchTripFieldQuery,
  usePatchTripByTokenMutation,
  useGetDriverLinksForTripQuery,
  useLazyGetDriverLinksForTripQuery,
  useExportTripsCSVMutation,
  usePatchTripDriverAckMutation,
  usePatchTripDriverAckByTokenMutation,
  util: tripApiUtil
} = tripApi;

export { persistentTripsAdapter, persistentTripsSelector };
